import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Space, message, Empty, Select, Tooltip } from 'antd';
import { formatMoney } from '../../../shared/utils/money';
import { ReloadOutlined, EyeOutlined, DollarOutlined } from '@ant-design/icons';
import billingService from '../../services/billingService';
import moment from 'moment';

import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';

const Invoices = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    // 服务端分页（默认 20，最大 100）
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [statusFilter, setStatusFilter] = useState(undefined);

    useEffect(() => {
        loadInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, statusFilter, i18n.language]); // 明细描述按语言由 API 渲染：切换语言重新拉

    const loadInvoices = async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (statusFilter) params.status = statusFilter;
            const response = await billingService.getInvoices(params);
            if (response.success) {
                setInvoices(Array.isArray(response.data) ? response.data : []);
                setTotal(response.pagination?.total ?? (Array.isArray(response.data) ? response.data.length : 0));
            } else {
                message.error(response.error || t('Failed to load invoices'));
            }
        } catch (error) {
            message.error(t('Failed to load invoices: {{value}}', { value: error.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (record) => {
        navigate(userPath(`/billing/invoices/${record.id}`));
    };

    const handlePay = (record) => {
        navigate(userPath(`/billing/invoices/${record.id}`));
    };

    const getStatusTag = (status) => {
        const statusMap = {
            draft:     { color: 'default',    text: t('Draft') },
            unpaid:    { color: 'warning',    text: t('Payment pending') },
            paid:      { color: 'success',    text: t('Paid') },
            cancelled: { color: 'default',    text: t('Canceled') },
            refunded:  { color: 'processing', text: t('Refunded') },
            void:      { color: 'error',      text: t('Voided') }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: t('Invoice number'),
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            width: 180,
            render: (text, record) => (
                <a onClick={() => handleViewDetail(record)}>{text}</a>
            ),
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: t('Amount'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 120,
            // 发票保持自身币种；未付且币种≠余额币种时悬停显示余额折算额
            render: (amount, record) => {
                const text = formatMoney(amount, record.currency || 'USD', { original: true });
                const eq = record.balance_equivalent;
                return eq && eq.amount != null
                    ? <Tooltip title={`≈ ${formatMoney(eq.amount, eq.currency, { original: true })}`}>{text}</Tooltip>
                    : text;
            }
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (
                <>
                    {getStatusTag(status)}
                    {status === 'unpaid' && record.payment_pending && (
                        <Tag color="processing" style={{ marginTop: 4 }}>
                            {record.payment_pending === 'awaiting_transfer' ? t('Awaiting transfer') : t('Processing')}
                        </Tag>
                    )}
                </>
            )
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (date) => moment(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: t('Expiration date'),
            dataIndex: 'due_date',
            key: 'due_date',
            width: 120,
            render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-'
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
                        {t('View')}
                    </Button>
                    {record.status === 'unpaid' && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<DollarOutlined />}
                            onClick={() => handlePay(record)}
                        >
                            {t('Pay now')}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    // Render a friendly empty state with cross-flow links
    const emptyState = (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('No invoice records yet')}
        >
            <Space>
                <Button type="primary" onClick={() => navigate(userPath('/billing/balance'))}>
                    {t('View balance')}
                </Button>
                <Button onClick={() => navigate(userPath('/billing/subscription'))}>
                    {t('My subscriptions')}
                </Button>
            </Space>
        </Empty>
    );

    return (
        <div>
            <Card
                title={t('Invoice management')}
                bordered={false}
                extra={
                    <Space>
                        <Select
                            allowClear
                            placeholder={t('All statuses')}
                            style={{ width: 120 }}
                            value={statusFilter}
                            onChange={(v) => { setPage(1); setStatusFilter(v); }}
                            options={[
                                { value: 'unpaid', label: t('Payment pending') },
                                { value: 'paid', label: t('Paid') },
                                { value: 'cancelled', label: t('Canceled') },
                                { value: 'refunded', label: t('Refunded') },
                            ]}
                        />
                        <Button icon={<ReloadOutlined />} onClick={loadInvoices} loading={loading}>
                            {t('Refresh')}
                        </Button>
                    </Space>
                }
            >
                <Table
                    onRow={rowLink(navigate, (r) => `/billing/invoices/${r.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
                    columns={columns}
                    dataSource={invoices}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 1100 }}
                    locale={{ emptyText: emptyState }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100'],
                        showTotal: (total) => t('{{total}} records total', { total }),
                        onChange: (p, ps) => {
                            if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); }
                        },
                    }}
                />
            </Card>
        </div>
    );
};

export default Invoices;
